import { AdminService, SignatureResoureModel } from "@/services/admin";
import {
	AnonymousCredential,
	BlobServiceClient,
	BlockBlobClient,
	BlockBlobParallelUploadOptions,
	newPipeline
} from "@azure/storage-blob";
import FileSaver from "file-saver";
import moment, { Moment } from "moment";
import { store } from "@/commonui/";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { MediaFileUploads, StatusType } from "../services/media-uploader/model";
import { getActualFileNameAndType } from "@/util/func";
import { MediaSlotContainerTypeMap } from "@/util/constants";
import { MediaSlot, MediaType } from "@/util/enum";
import { MediaUploadTempFolder } from "@/views/text-guardian/sub-views/title-list/sub-views/media/utils/const";

@Module({ namespaced: true, name: "MediaUpload", store: store, dynamic: true })
export class MediaUploadState extends VuexModule {
	// SAS Url information
	sasURL: SignatureResoureModel[] | null = null;
	sasURLFetchedAt: Moment | null = null;
	downloadSASURL: SignatureResoureModel[] | null = null;
	downloadSASURLFetchAt: Moment | null = null;
	// image upload infor
	fileUploads: MediaFileUploads[] = []; // keeps track of nodes for which images are being uploaded
	@Mutation
	resetState() {
		this.fileUploads = [];
	}

	@Mutation
	setUploadsRemaining(value: MediaFileUploads[]) {
		this.fileUploads = value;
	}

	@Mutation
	setSasURL(value: SignatureResoureModel[]) {
		this.sasURL = value;
		this.sasURLFetchedAt = moment();
	}

	@Mutation
	setDownloadSasURL(value: SignatureResoureModel[]) {
		this.downloadSASURL = value;
		this.downloadSASURLFetchAt = moment();
	}

	@Mutation
	updateUploadProgress(args: { loadedBytes: number; uploadId: string; self: MediaUploadState }) {
		const { loadedBytes, uploadId, self } = args;
		const uploads = self.fileUploads.map((f) => {
			if (f.id === uploadId) {
				f.uploaded = loadedBytes;
				f.progress = (loadedBytes * 100) / f.size;

				if (f.progress == 100) {
					f.status = StatusType.Completed;
				}
			}
			return f;
		});

		self.setUploadsRemaining(uploads);
	}

	@Action
	async uploadFile(payload: {
		id: string;
		file: File;
		languageCode?: string;
		length?: number;
		slotId: MediaSlot;
		pageId?: string;
	}): Promise<void> {
		const { file, id, languageCode, length, slotId, pageId } = payload;
		const uploadId = id;

		const blobPath = await this.getFilePath({
			mediaId: id,
			file
		});

		try {
			this.setUploadsRemaining([
				...this.fileUploads,
				{
					id: id,
					fileName: file.name,
					filePath: blobPath,
					size: file.size,
					length: length,
					uploaded: 0,
					progress: 0,
					status: StatusType.Progress,
					languageCode: languageCode,
					pageId: pageId
				}
			]);

			await this.getSasURL();
			const sasURL = this.sasURL?.find(
				(f) => f.type === MediaSlotContainerTypeMap[slotId]
			) as SignatureResoureModel;

			const blockBlobClient = await this.generateBlockBlobClient({
				sasUrl: sasURL.url,
				container: sasURL.name,
				path: blobPath
			});
			const uploadId = id;
			const parallelOptions: BlockBlobParallelUploadOptions = {
				concurrency: 5,
				onProgress: ({ loadedBytes }) => {
					this.updateUploadProgress({ loadedBytes, uploadId, self: this });
				}
			};
			await blockBlobClient.uploadData(file, parallelOptions);

			// this.setUploadsRemaining(this.fileUploads.filter((f) => f.id !== uploadId));
		} catch (e) {
			console.log("Unable to upload file.", e, this);
			// this.setUploadsRemaining(this.fileUploads.filter((f) => f.id !== uploadId));
		}
	}

	@Action
	async downloadFile(args: { url: string; slotId: MediaSlot }) {
		const { url, slotId } = args;
		await this.getDownloadSasURL(); // gets the sas url
		const sasURL = this.downloadSASURL?.find(
			(f) => f.type === MediaSlotContainerTypeMap[slotId]
		) as SignatureResoureModel;
		const blobURL = await this.generateBlockBlobClient({ sasUrl: sasURL.url, container: sasURL.name, path: url });
		const request = await blobURL.download();
		const blob = await request.blobBody;
		if (blob) {
			const fileType = getActualFileNameAndType(url);
			FileSaver.saveAs(blob, "download" + fileType);
		} else {
			throw "File is not found.";
		}
	}

	@Action
	async generateBlockBlobClient(args: { sasUrl: string; container: string; path: string }): Promise<BlockBlobClient> {
		const { sasUrl, container, path } = args;
		const pipeline = newPipeline(new AnonymousCredential(), {
			retryOptions: { maxTries: 4 } // Retry options
		});
		const serviceClient = new BlobServiceClient(sasUrl, pipeline);
		const containerClient = serviceClient.getContainerClient(container);
		return containerClient.getBlockBlobClient(path);
	}

	@Action
	async getSasURL() {
		// gets sas URL for uploads
		if (!this.sasURL || moment(this.sasURLFetchedAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setSasURL(await AdminService.getUploadSasURL());
		}
	}

	@Action
	async getDownloadSasURL() {
		// gets sas URL for uploads
		if (!this.downloadSASURL || moment(this.downloadSASURLFetchAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setDownloadSasURL(await AdminService.getDownloadSasURL());
		}
	}

	@Action
	getFilePath(args: { mediaId: string; file: File }): string {
		const { mediaId, file } = args;
		const nameParts = file.name.split(".");
		const extension = nameParts[nameParts.length - 1];
		const fileNewName = mediaId + "." + extension;
		return `${MediaUploadTempFolder}/${fileNewName}`;
	}
}

export const MediaUploadModule = getModule(MediaUploadState);
